import { FORM_CONSTANTS } from "../constants";
import { IDepdendencyItem, IForm } from "../constants/common-interface";
import MetaForm from "../constants/MetaForm";
import { IDependency, IField, IURLLoaderConfig } from "../constants/model-interfaces";
import FormUtils from "./FormUtil";
import ValidationUtil from "./ValidationUtil";

class DependencyUtil {
    static initDependencies(form: IForm, section: string, fields: Array<IField>) {
        fields &&
            fields.forEach((field) => {
                if (field && field?.meta?.dependencies) {
                    this.setDependencies(form, section, field.name, field?.meta?.dependencies);
                }
                if (field.fields && field.fields.length > 0) {
                    this.initDependencies(form, field.name, field.fields);
                }
            });
    }

    static getDependencies(form: IForm, section: string, field: string) {
        // eslint-disable-next-line dot-notation
        return form[section][field]["dependencies"];
    }

    static setDependencies(form: IForm, section: string, field: string, dependencies: IDependency) {
        if (!dependencies) return;
        Object.keys(dependencies).forEach((type) => {
            if (dependencies[type] instanceof Array) {
                dependencies[type].forEach((dependency: IDepdendencyItem) => {
                    this.setDependency(form, dependency, section, type, field);
                });
            } else {
                this.setDependency(form, dependencies[type], section, type, field);
            }
        });
    }

    static setDependency(form: IForm, dependency: IDepdendencyItem, section: string, type: string, field: string) {
        const ref = dependency.ref || "";
        const formSection = dependency?.section || section;
        const value = dependency?.value;
        const valueFn = dependency?.valueFn;
        // dependency key
        const D_KEY = FORM_CONSTANTS.DEPENDENCY_KEY;
        if (ref && !form[formSection][ref][D_KEY]) {
            form[formSection][ref][D_KEY] = [];
        }
        let extraParams = {};
        switch (type) {
            case "patternMatchUrlLoader":
            case "pattern_match_url_loader":
                extraParams = {
                    pattern: dependency.pattern,
                    changeType: dependency.type,
                    url_type: dependency.url_type,
                    defaultValue: dependency.defaultValue
                };
            // eslint-disable-next-line no-fallthrough
            case "load":
                {
                    const url = dependency.url;
                    const labelKey = dependency?.labelKey;
                    const valueKey = dependency?.valueKey;
                    const responseKey = dependency.responseKey;
                    const queryParams = dependency.queryParams;
                    const pathParams = dependency.pathParams;
                    form[formSection][ref][D_KEY].push({
                        section,
                        type,
                        field,
                        url,
                        valueKey,
                        labelKey,
                        responseKey,
                        queryParams,
                        pathParams,
                        ...extraParams
                    });
                }
                break;
            case "loadOptions":
            case "load_options":
                {
                    const valueMap = dependency.valueMap;
                    form[formSection][ref][D_KEY].push({
                        section,
                        type,
                        field,
                        valueMap
                    });
                }
                break;
            case "equals":
                // eslint-disable-next-line no-lone-blocks
                {
                    form[formSection][ref][D_KEY].push({
                        section,
                        type,
                        field,
                        value,
                        currentValue: dependency.currentValue,
                        resetValue: dependency.resetValue
                    });
                }
                break;
            case "validation":
            case "change":
                {
                    const changeType = dependency.type;
                    const valueMap = dependency.valueMap;
                    form[formSection][ref][D_KEY].push({
                        section,
                        type,
                        field,
                        value,
                        valueMap,
                        changeType
                    });
                }
                break;
            case "prop_update":
                {
                    const propValue = dependency.propValue;
                    const propName = dependency.propName;
                    const valueMap = dependency.valueMap;
                    form[formSection][ref][D_KEY].push({
                        section,
                        type,
                        field,
                        value,
                        valueMap,
                        propName,
                        propValue
                    });
                }
                break;
            case "exists":
                extraParams = {
                    condition: dependency.condition
                };
                form[formSection][ref][D_KEY].push({
                    section,
                    type,
                    field,
                    value,
                    valueFn,
                    ...extraParams
                });
                break;
            default:
                form[formSection][ref][D_KEY].push({
                    section,
                    type,
                    field,
                    value,
                    valueFn
                });
        }
    }

    static handleDependencies(metaform: MetaForm, section: string, fieldName: string, value: any, fieldDisplayed: boolean) {
        // eslint-disable-next-line promise/param-names
        return new Promise((sucessCallback: Function) => {
            const deps = this.getDependencies(metaform.form, section, fieldName);
            const totalDeps = deps && deps.length > 0 ? deps.length : 0;
            if (totalDeps === 0) {
                sucessCallback(true);
                return;
            }
            let resolvedDepCount = 0;
            const resolved: IResolved = {
                next: () => {
                    resolvedDepCount++;
                    if (resolvedDepCount === totalDeps) {
                        sucessCallback();
                    }
                }
            };
            deps &&
                deps.forEach((dep: IDepdendencyItem) => {
                    const type = dep.type;
                    switch (type) {
                        case "exists":
                            {
                                const depValue = dep.value;
                                const field = dep.field;
                                if (fieldDisplayed) {
                                    if (value !== undefined) {
                                        let fieldDisplayed;
                                        if (depValue === undefined) {
                                            // only field dependency
                                            fieldDisplayed = true;
                                            metaform.setFieldDisplay(dep.section, field, fieldDisplayed);
                                        } else {
                                            // condition dependency
                                            let conditionMatch = false;
                                            if (Array.isArray(depValue)) {
                                                if (depValue.indexOf(value) >= 0) {
                                                    conditionMatch = true;
                                                }
                                            } else {
                                                switch (depValue) {
                                                    case FORM_CONSTANTS.INTERNAL_VALUES.NOT_EMPTY:
                                                        conditionMatch = value !== "";
                                                        break;
                                                    case FORM_CONSTANTS.INTERNAL_VALUES.VALUE_FN:
                                                        {
                                                            const valueFn = dep.valueFn;
                                                            if (valueFn) {
                                                                const fn = metaform.getFn(valueFn);
                                                                if (fn) {
                                                                    conditionMatch = fn(value);
                                                                }
                                                            }
                                                        }
                                                        break;
                                                    case "$condition":
                                                        if (dep.condition) {
                                                            conditionMatch = ValidationUtil.parseCondition(
                                                                metaform.form,
                                                                dep.condition,
                                                                dep.section || section
                                                            );
                                                        }
                                                        break;
                                                    default:
                                                        conditionMatch = value === depValue;
                                                }
                                            }
                                            fieldDisplayed = conditionMatch;
                                            metaform.setFieldDisplay(dep.section, field, conditionMatch);
                                        }
                                        const subfield = metaform.getField(dep.section, field);
                                        // cascade
                                        this.handleDependencies(metaform, dep.section, field, subfield.value, fieldDisplayed).then(() => resolved.next());
                                    } else {
                                        resolved.next();
                                    }
                                } else {
                                    metaform.setFieldDisplay(dep.section, field, false);
                                    const subfield = metaform.getField(dep.section, field);
                                    // cascade
                                    this.handleDependencies(metaform, dep.section, field, subfield.value, false).then(() => resolved.next());
                                }
                            }
                            break;
                        case "load":
                            {
                                const field = dep.field;
                                // const url = dep.url.replace('{0}', value);
                                if (value && dep.url) {
                                    const field = dep.field;
                                    const queryParams = dep.queryParams || [];
                                    // reset options
                                    metaform.setFieldOptions(dep.section, field, []);
                                    metaform
                                        .api("get", dep.url, queryParams, value, section)
                                        .then((response) => {
                                            const results = dep.responseKey ? response[dep.responseKey] : response;
                                            let newResults = [];
                                            if (dep.labelKey && dep.valueKey) {
                                                newResults = results.map((r: any) => {
                                                    return {
                                                        label: r[dep.labelKey!],
                                                        value: r[dep.valueKey!],
                                                        ref: r
                                                    };
                                                });
                                            } else {
                                                newResults = results;
                                            }
                                            metaform.setFieldOptions(dep.section, field, newResults);
                                            resolved.next();
                                        })
                                        .catch(() => {
                                            resolved.next();
                                        });
                                } else {
                                    resolved.next();
                                }
                            }
                            break;
                        case "loadOptions":
                        case "load_options":
                            {
                                const field = dep.field;
                                const val = dep.value;
                                const valueMap = dep.valueMap;
                                if (valueMap !== undefined) {
                                    if (valueMap[value]) {
                                        metaform.setFieldOptions(dep.section, field, valueMap[value]);
                                    }
                                } else {
                                    if (value) {
                                        // to do
                                    }
                                }
                                resolved.next();
                            }
                            break;
                        case "enabled":
                            {
                                const field = dep.field;
                                metaform.setFieldDisabled(dep.section, field, value !== dep.value);
                                resolved.next();
                            }
                            break;
                        case "equals":
                            {
                                const field = dep.field;
                                if (value === dep.value) {
                                    metaform.setField(dep.section, field, dep.currentValue);
                                } else if (dep.resetValue !== undefined) {
                                    metaform.setField(dep.section, field, dep.resetValue);
                                }
                                resolved.next();
                            }
                            break;
                        case "change":
                            {
                                const changeType = dep.changeType;
                                if (changeType) {
                                    switch (changeType) {
                                        case "setter":
                                            if (dep.valueMap !== undefined && dep.valueMap[value] !== undefined) {
                                                metaform.setField(dep.section, dep.field, dep.valueMap[value]);
                                                const field = metaform.getField(dep.section, dep.field);
                                                this.handleDependencies(metaform, dep.section, dep.field, dep.valueMap[value], field.display).then(() =>
                                                    resolved.next()
                                                );
                                            } else {
                                                resolved.next();
                                            }
                                            break;
                                        default:
                                            resolved.next();
                                    }
                                } else {
                                    resolved.next();
                                }
                            }
                            break;
                        case "validation":
                            {
                                const fieldProp = metaform.getFieldProp(dep.section, dep.field, dep.type);
                                fieldProp[dep.changeType] = dep.valueMap[value];
                                metaform.setFieldProp(dep.section, dep.field, dep.type, fieldProp);
                                resolved.next();
                            }
                            break;
                        case "patternMatchUrlLoader":
                        case "pattern_match_url_loader":
                            {
                                const changeType = dep.changeType;
                                if (dep.pattern) {
                                    const pattern = new RegExp(dep.pattern);
                                    // test pattern and proceed
                                    if (pattern.test(value)) {
                                        if (changeType) {
                                            switch (changeType) {
                                                case "url_loader":
                                                    {
                                                        const itemConfig: IURLLoaderConfig = {
                                                            type: "url_loader",
                                                            url: dep.url,
                                                            url_type: dep.url_type,
                                                            labelKey: dep.labelKey,
                                                            valueKey: dep.valueKey,
                                                            queryParams: dep.queryParams,
                                                            pathParams: dep.pathParams
                                                        };
                                                        metaform
                                                            .getData(itemConfig, value, dep.section, FORM_CONSTANTS.INTERNAL_VALUES.INPUT)
                                                            .then((results) => {
                                                                if (results && results.length > 0) {
                                                                    const fieldDisplayType = metaform.getFieldProp(dep.section, dep.field, "displayType");
                                                                    metaform.setDisplayTypeFieldProp(fieldDisplayType, dep.section, dep.field, results, dep);
                                                                }
                                                                resolved.next();
                                                            })
                                                            .catch((err) => {
                                                                metaform.handleError(err, dep.section, dep.field);
                                                                resolved.next();
                                                            });
                                                    }
                                                    break;
                                                default:
                                                    resolved.next();
                                            }
                                        } else {
                                            resolved.next();
                                        }
                                    } else {
                                        resolved.next();
                                    }
                                } else {
                                    resolved.next();
                                }
                            }
                            break;
                        case "prop_update":
                            // condition match
                            if (dep.value !== undefined) {
                                if (value === dep.value) {
                                    metaform.setFieldProp(dep.section, dep.field, dep.propName, dep.propValue);
                                    resolved.next();
                                } else {
                                    resolved.next();
                                }
                            } else if (dep.valueMap !== undefined) {
                                this.handleValueMap(metaform, value, dep, resolved);
                            }
                            break;
                        default:
                            resolved.next();
                    }
                });
        });
    }

    static handleValueMap(metaform: MetaForm, value: any, dep: IDepdendencyItem, resolved: IResolved) {
        if (dep.valueMap !== undefined) {
            const mappedValue = dep.valueMap[value];
            const defaultMappedValue = dep.valueMap[FORM_CONSTANTS.INTERNAL_VALUES.DEFAULT];
            const actualMappedValue = mappedValue ?? defaultMappedValue;
            if (actualMappedValue !== undefined) {
                if (typeof actualMappedValue === "object") {
                    // parse object
                    this.handleValueMapObject(metaform, value, dep, resolved, actualMappedValue);
                } else {
                    metaform.setFieldProp(dep.section, dep.field, dep.propName, actualMappedValue);
                    resolved.next();
                }
            } else {
                resolved.next();
            }
        }
    }

    static handleValueMapObject(metaform: MetaForm, value: any, dep: IDepdendencyItem, resolved: IResolved, mappedValue: any) {
        const type = mappedValue.type;
        if (type !== undefined) {
            switch (type) {
                case "fieldValue":
                    {
                        const ref = mappedValue.ref;
                        const fieldValue = metaform.getFieldProp(mappedValue.section || dep.section, ref, "value");
                        metaform.setFieldProp(dep.section, dep.field, dep.propName, fieldValue);
                        resolved.next();
                    }
                    break;
                case "fieldProp":
                    {
                        const ref = mappedValue.ref;
                        const prop = mappedValue.propName;
                        const fieldValue = metaform.getFieldProp(mappedValue.section || dep.section, ref, prop);
                        metaform.setFieldProp(dep.section, dep.field, dep.propName, fieldValue);
                        resolved.next();
                    }
                    break;
                default:
                    resolved.next();
            }
        } else {
            resolved.next();
        }
    }
}

interface IResolved {
    next: () => void
}

export default DependencyUtil;
